import {
  DUITNOW_AUTODEBIT_LABEL,
  getDuitNowMethodLabel,
  isDuitNowMethod
} from '../constants/duitnow.const';
import {
  ALI_PAY_LOGO_PATH,
  GCASH_LOGO_PATH,
  TRUE_MONEY_LOGO_PATH,
  WECAHT_PAY_LOGO_PATH
} from '../constants/logo';
import {
  genericGlyphOf,
  knownIconAsset,
  paymentMethodIconAsset,
  type LogoSurface
} from '../constants/payment-method-icon.const';
import { resolvePaymentMethodConfig } from '../constants/payment-method-logo.const';
import type {
  PaymentMethodInfo,
  transactionData
} from '../core/api/http-methods/get-user-transactions';
import type { PaymentConsent } from '../types/payment';
import type { PaymentMethodDisplay } from '../types/payment-method-display';

/*
 * How each screen names a payment method. When the backend sends `payment_method_display`
 * the screens render its label, detail and icon and nothing else. When it does not (a backend
 * from before that field), every function here returns exactly what the screen showed before
 * it, so an older backend renders unchanged.
 */

type WithDisplay = {
  payment_method_display?: PaymentMethodDisplay | null;
};

/** The backend's description, or null when it sent none. */
export const displayOf = (
  row: WithDisplay | null | undefined
): PaymentMethodDisplay | null => row?.payment_method_display ?? null;

/** "Visa · •••• 4242", or just the label when there is no detail. */
export const labelWithDetail = (display: PaymentMethodDisplay): string =>
  display.detail ? `${display.label} · ${display.detail}` : display.label;

/**
 * What to draw for a payment method's logo:
 * - `asset`: an image.
 * - `generic`: a glyph standing in for a method the portal has no logo for.
 * - `missing`: only from the older-backend path, for a brand it never had a logo for; it
 *   renders an image with no source, exactly as that path always has.
 */
export type LogoView =
  | { kind: 'asset'; src: string; alt: string }
  | { kind: 'generic'; glyph: 'card' | 'wallet'; alt: string }
  | { kind: 'missing'; alt: string };

const logoOf = (
  display: PaymentMethodDisplay,
  surface: LogoSurface
): LogoView => {
  const src = paymentMethodIconAsset(display.icon, surface);
  return src
    ? { kind: 'asset', src, alt: display.label }
    : {
        kind: 'generic',
        glyph: genericGlyphOf(display.icon),
        alt: display.label
      };
};

/** An older-backend logo looked up in a table: the asset, or the sourceless image. */
const legacyLogo = (src: string | undefined, alt: string): LogoView =>
  src ? { kind: 'asset', src, alt } : { kind: 'missing', alt };

// ── Activity list ────────────────────────────────────────────────────────────

/** The "via …" line of a charge or refund in the activity list. */
export const activityMethodLine = (
  row: Pick<transactionData, 'payment_method'> & WithDisplay
): { text: string; logo: LogoView | null } => {
  const display = displayOf(row);
  if (!display) return { text: 'via ' + row.payment_method, logo: null };
  return {
    text: `via ${labelWithDetail(display)}`,
    logo: logoOf(display, 'light')
  };
};

/** The line under an activity row's title. */
export type ActivityRowLine =
  | { kind: 'loyalty' }
  | { kind: 'method'; text: string; logo: LogoView | null }
  | { kind: 'none' };

/**
 * The line under an activity row's title:
 * - "Loyalty Recorded" only for a loyalty-only order, which the backend describes as `loyalty`.
 * - Any other order (a payment order still pending, canceled or failed) names the method the
 *   backend describes, or shows no line when there is no method to name.
 * - A charge or refund reads "via" + its method, as before.
 * An older backend sends no description at all and cannot tell the orders apart; it keeps
 * "Loyalty Recorded" for every order, as it always showed.
 */
export const activityRowLine = (
  row: Pick<transactionData, 'record_type' | 'payment_method'> & WithDisplay
): ActivityRowLine => {
  if (row.record_type === 'order') {
    if (row.payment_method_display === undefined) return { kind: 'loyalty' };
    const display = displayOf(row);
    if (!display) return { kind: 'none' };
    if (display.value === 'loyalty') return { kind: 'loyalty' };
  }
  return { kind: 'method', ...activityMethodLine(row) };
};

// ── Transaction detail ───────────────────────────────────────────────────────

/** Caption of the row that names which AutoDebit account paid, by method value. */
const AUTODEBIT_DETAIL_CAPTIONS: Readonly<Record<string, string>> = {
  duitnow_id: 'DuitNow ID',
  duitnow_consent: 'Bank'
};

export interface TransactionMethodView {
  label: string;
  /** The line under the label. */
  detail: string | null;
  logo: LogoView | null;
  /** A row of its own under the payment method: which AutoDebit account paid. */
  autoDebitRow: { caption: string; value: string } | null;
}

/**
 * The "Payment Method" field of a transaction, or null when there is nothing to show: a
 * loyalty description, or neither a description nor a payment method in the older field. An
 * order that was never paid has no older payment method but may still be described (e.g. a
 * pending Pay Separately order). An AutoDebit charge names its account in a row of its own
 * rather than under the label.
 */
export const transactionMethodView = (
  paymentMethod: PaymentMethodInfo | null,
  display: PaymentMethodDisplay | null | undefined
): TransactionMethodView | null => {
  if (display?.value === 'loyalty') return null;
  if (display) {
    const caption = Object.prototype.hasOwnProperty.call(
      AUTODEBIT_DETAIL_CAPTIONS,
      display.value
    )
      ? AUTODEBIT_DETAIL_CAPTIONS[display.value]
      : null;
    const autoDebitRow =
      caption && display.detail ? { caption, value: display.detail } : null;
    return {
      label: display.label,
      detail: caption ? null : display.detail,
      logo: logoOf(display, 'light'),
      autoDebitRow
    };
  }
  if (!paymentMethod) return null;
  const config = resolvePaymentMethodConfig(paymentMethod.brand);
  return {
    label: paymentMethod.method,
    detail: paymentMethod.detail || null,
    logo: config
      ? { kind: 'asset', src: config.logo, alt: config.label }
      : null,
    autoDebitRow: null
  };
};

// ── Saved-method cards on the home page ──────────────────────────────────────

export interface SavedMethodView {
  /** The "Method" heading. */
  name: string;
  /** The backend's detail; only set when it described the method. */
  detail: string | null;
  logo: LogoView;
  /** Which logo height to use. */
  logoSizeKey: string;
  /** How the set-preferred dialog names the method. */
  dialogName: string;
  fromDisplay: boolean;
}

/** Older backend: card-face logo by lower-cased card_brand. */
const CARD_LOGOS: Readonly<Record<string, string>> = {
  visa: knownIconAsset('visa', 'brand'),
  unionpay: knownIconAsset('unionpay', 'brand'),
  mastercard: knownIconAsset('mastercard', 'brand'),
  amex: knownIconAsset('amex', 'brand')
};

/** Older backend: card-face logo by lower-cased method. */
const EWALLET_LOGOS: Readonly<Record<string, string>> = {
  'tng-ewallet': knownIconAsset('tng', 'brand'),
  'grabpay-ewallet': knownIconAsset('grabpay', 'brand'),
  grabpay: knownIconAsset('grabpay', 'brand'),
  'gcash-ewallet': GCASH_LOGO_PATH,
  shopeepay: knownIconAsset('shopeepay', 'brand'),
  'truemoney-ewallet': TRUE_MONEY_LOGO_PATH,
  'wechatpay-ewallet': WECAHT_PAY_LOGO_PATH,
  'alipay-ewallet': ALI_PAY_LOGO_PATH
};

const fromDisplay = (display: PaymentMethodDisplay): SavedMethodView => ({
  name: display.label,
  detail: display.detail,
  logo: logoOf(display, 'brand'),
  logoSizeKey: display.icon,
  dialogName: labelWithDetail(display),
  fromDisplay: true
});

/** A saved card. */
export const cardMethodView = (consent: PaymentConsent): SavedMethodView => {
  const display = displayOf(consent);
  if (display) return fromDisplay(display);
  const brand = consent?.card_brand?.toLocaleLowerCase() || '';
  return {
    name: consent?.method,
    detail: null,
    logo: legacyLogo(CARD_LOGOS[brand], consent?.card_brand || 'Payment Brand'),
    logoSizeKey: brand,
    dialogName: consent?.method || '',
    fromDisplay: false
  };
};

/** A saved e-wallet. */
export const eWalletMethodView = (consent: PaymentConsent): SavedMethodView => {
  const display = displayOf(consent);
  if (display) return fromDisplay(display);
  const method = consent?.method?.toLocaleLowerCase() || '';
  return {
    name: consent?.method,
    detail: null,
    logo: legacyLogo(EWALLET_LOGOS[method], 'Brand Logo'),
    logoSizeKey: method,
    dialogName: 'TNG-EWALLET',
    fromDisplay: false
  };
};

/** A saved DuitNow AutoDebit consent. */
export const duitNowMethodView = (
  consent: PaymentConsent
): SavedMethodView & { legacyLines: [string, string] | null } => {
  const display = displayOf(consent);
  if (display) return { ...fromDisplay(display), legacyLines: null };
  return {
    name: DUITNOW_AUTODEBIT_LABEL,
    detail: null,
    logo: {
      kind: 'asset',
      src: knownIconAsset('duitnow', 'brand'),
      alt: 'Duitnow Logo'
    },
    logoSizeKey: 'duitnow',
    dialogName: DUITNOW_AUTODEBIT_LABEL,
    fromDisplay: false,
    legacyLines: [
      getDuitNowMethodLabel(consent),
      consent?.duitnow_id_masked ?? ''
    ]
  };
};

// ── Expiry bar ───────────────────────────────────────────────────────────────

/** How the expiry bar names one saved method in its sentence. */
export const expiryMethodName = (consent: PaymentConsent): string => {
  const display = displayOf(consent);
  if (display) return labelWithDetail(display);
  return consent.method === 'CARD' || consent.method === 'debit'
    ? `card ending in ${consent.card_last4}`
    : isDuitNowMethod(consent.method)
      ? DUITNOW_AUTODEBIT_LABEL
      : consent.method
          .replace('tng-ewallet', 'TNG eWallet')
          .replace('grabpay', 'Grabpay');
};
